import React, { useMemo } from 'react';
import { SxProps, Tooltip } from '@mui/material';
import { CaptionBox, CaptionTextField, ImageCaption } from './style';
import { Attachment } from '../AttachmentDisplay/type';
import { FileNameTypes } from '@/__generated__/graphql';
import { theme } from '@/theme/theme';
import { CAPTION_DISPLAY_TRUNCATE, CAPTION_MAX_LENGTH } from '@/constants';
import { truncateFilenameFromMiddle } from '@/utils';

type AttachmentCaptionBoxProps = {
  isUploadDialog?: boolean;
  isEditing: boolean;
  collectionName: string;
  attachmentCaption: string | undefined;
  setAttachmentCaption: (caption: string) => void;
  handleAttachmentCaption: () => void;
  setIsEditing: (editing: boolean) => void;
  handleCaption: (e: React.MouseEvent) => void;
  selectedAttachment: Attachment;
  isViewPoint?: boolean;
  sx?: SxProps;
  handleViewIn3D?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  location?: string;
  fluid?: boolean;
  fallbackText?: string;
  showToolTip?: boolean;
  disableEnterSave?: boolean;
  onCaptionChange?: (caption: string) => void;
  truncateMiddle?: boolean;
  readOnly?: boolean;
};

export const AttachmentCaption = ({
  isUploadDialog,
  isEditing,
  collectionName,
  attachmentCaption,
  setAttachmentCaption,
  handleAttachmentCaption,
  setIsEditing,
  handleCaption,
  selectedAttachment,
  isViewPoint,
  sx,
  handleViewIn3D,
  fallbackText,
  location = '',
  fluid = false,
  disableEnterSave = false,
  onCaptionChange,
  truncateMiddle = false,
  readOnly = false,
}: AttachmentCaptionBoxProps) => {
  const { baseCaption, attachmentName } = useMemo(() => {
    const base =
      attachmentCaption ??
      selectedAttachment.caption ??
      fallbackText ??
      selectedAttachment.fileName ??
      selectedAttachment.name ??
      '';

    const isReference =
      selectedAttachment.name === FileNameTypes.ReferenceDrawing ||
      selectedAttachment.name === FileNameTypes.IsometricDrawing;

    const finalName = isReference ? `${base} (Reference Drawing)` : base;
    return { baseCaption: base, attachmentName: finalName };
  }, [attachmentCaption, selectedAttachment, fallbackText]);

  const displayAttachmentName = truncateMiddle
    ? truncateFilenameFromMiddle(attachmentName ?? '')
    : (attachmentName ?? '');

  const shouldShowTooltip = useMemo(() => {
    const base = baseCaption ?? '';
    const threeLineThreshold = CAPTION_DISPLAY_TRUNCATE * 3;
    return base.length > threeLineThreshold;
  }, [baseCaption]);

  return (
    <CaptionBox onClick={handleCaption} sx={readOnly ? { justifyContent: 'flex-start' } : {}}>
      {!isUploadDialog && isEditing && collectionName !== 'paintRegion' ? (
        <CaptionTextField
          fluid={fluid}
          className={`captionTextField ${location}-attachment-caption-edit`}
          sx={sx}
          value={attachmentCaption ?? attachmentName}
          size="small"
          onChange={(e) => setAttachmentCaption(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !disableEnterSave) handleAttachmentCaption();
          }}
          onBlur={() => {
            if (disableEnterSave && onCaptionChange && attachmentCaption) {
              onCaptionChange(attachmentCaption);
            }
            setIsEditing(false);
          }}
          autoFocus
          multiline
          variant="outlined"
          slotProps={{
            input: {
              inputProps: { maxLength: CAPTION_MAX_LENGTH },
            },
          }}
          error={(attachmentCaption?.length ?? 0) >= CAPTION_MAX_LENGTH}
          helperText={
            (attachmentCaption?.length ?? 0) >= CAPTION_MAX_LENGTH
              ? `Maximum ${CAPTION_MAX_LENGTH} characters reached`
              : `${attachmentCaption?.length ?? 0}/${CAPTION_MAX_LENGTH}`
          }
        />
      ) : (
        <Tooltip
          arrow
          title={
            isViewPoint
              ? 'Ctrl+Click to open in new tab'
              : baseCaption === fallbackText && !!fallbackText
                ? ''
                : shouldShowTooltip
                  ? attachmentName
                  : ''
          }
          placement="top"
        >
          <ImageCaption
            fluid={fluid}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              isViewPoint ? handleViewIn3D?.(event) : setIsEditing(true);
            }}
            style={
              isViewPoint
                ? {
                    color: theme.palette.common.darkBlue,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }
                : {}
            }
          >
            {displayAttachmentName}
          </ImageCaption>
        </Tooltip>
      )}
    </CaptionBox>
  );
};
